import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Lock, User, Upload, Trash2, Plus, Eye, EyeOff, X, Save, ArrowLeft, ExternalLink, ImagePlus, FileText } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api/kennel";
const DEV_LOGIN = "ania";
const DEV_PASSCODE = "Katzenberg-Blog-2026";

const CATEGORIES = ["Szczenięta", "Szkolenia", "Wystawy", "Codzienność", "Socjalizacja", "Przygotowania do miotu", "Nasze psy"];

const emptyPost = () => ({
  title: "", slug: "", category: "Codzienność", date: new Date().toISOString().slice(0, 10),
  excerpt: "", cover_image: "", image_alt_text: "", content: "", gallery: [], videos: [],
  seo_title: "", seo_description: "", og_title: "", og_description: "", og_image: "", status: "draft",
});

const Field = ({ label, hint, children }) => (
  <div className="mb-5">
    <label className="block text-[11px] tracking-[0.12em] uppercase text-[#D4AF37]/70 mb-2">{label}</label>
    {children}
    {hint && <p className="text-[10px] text-[#B8B8B8]/40 mt-1.5">{hint}</p>}
  </div>
);

const inputCls = "w-full bg-[#0B0B0D] border border-[#2A2A2D] text-[#F5F5F5] text-sm px-3 py-2.5 focus:border-[#D4AF37] outline-none";

const KennelAdminBlog = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginForm, setLoginForm] = useState({ login: "", password: "" });
  const [loginError, setLoginError] = useState("");

  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // post object or null
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const coverRef = useRef(null);
  const ogRef = useRef(null);
  const galleryRef = useRef(null);
  const videoRef = useRef(null);
  const [videoProgress, setVideoProgress] = useState(0);

  useEffect(() => {
    if (sessionStorage.getItem("kennel_blog_dev") === "true" || sessionStorage.getItem("kennel_admin_logged") === "true") setIsLoggedIn(true);
    const existing = document.querySelector('meta[name="robots"]');
    const prev = existing ? existing.getAttribute("content") : null;
    let meta = existing;
    if (!meta) { meta = document.createElement("meta"); meta.name = "robots"; document.head.appendChild(meta); }
    meta.setAttribute("content", "noindex, nofollow");
    document.title = "Panel redakcyjny — Z życia hodowli";
    return () => {
      if (prev !== null) meta.setAttribute("content", prev);
      else meta.remove();
    };
  }, []);

  const fetchPosts = async () => {
    try { const r = await fetch(`${API}/admin/blog`); setPosts(await r.json()); } catch {}
  };
  useEffect(() => { if (isLoggedIn) fetchPosts(); }, [isLoggedIn]);

  const handleLogin = (e) => {
    e.preventDefault();
    const ok = (loginForm.login === DEV_LOGIN && loginForm.password === DEV_PASSCODE) ||
      (loginForm.login === "Martyna" && loginForm.password === "Martyna");
    if (ok) {
      setIsLoggedIn(true);
      sessionStorage.setItem("kennel_blog_dev", "true");
    } else setLoginError("Nieprawidłowe dane dostępu");
  };

  const uploadImage = async (file, slug) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("slug", slug || editing?.slug || editing?.title || "");
    const r = await fetch(`${API}/admin/blog/upload`, { method: "POST", body: fd });
    const d = await r.json();
    return d.url;
  };

  const handleCover = async (file) => {
    if (!file) return;
    setUploading("cover");
    const url = await uploadImage(file);
    setEditing(p => ({ ...p, cover_image: url }));
    setUploading("");
  };

  const handleOg = async (file) => {
    if (!file) return;
    setUploading("og");
    const url = await uploadImage(file);
    setEditing(p => ({ ...p, og_image: url }));
    setUploading("");
  };

  const handleGallery = async (files) => {
    if (!files.length) return;
    setUploading("gallery");
    const added = [];
    for (const f of files) {
      const url = await uploadImage(f);
      added.push({ url, alt: "" });
    }
    setEditing(p => ({ ...p, gallery: [...(p.gallery || []), ...added] }));
    setUploading("");
    if (galleryRef.current) galleryRef.current.value = "";
  };

  const CHUNK_SIZE = 4 * 1024 * 1024;
  const handleVideo = async (file) => {
    if (!file) return;
    setUploading("video");
    setVideoProgress(0);
    try {
      const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const total = Math.ceil(file.size / CHUNK_SIZE);
      let url = "";
      for (let i = 0; i < total; i++) {
        const chunk = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const fd = new FormData();
        fd.append("file", chunk, "chunk");
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", String(i));
        fd.append("total_chunks", String(total));
        fd.append("slug", editing?.slug || editing?.title || "");
        fd.append("filename", file.name);
        const r = await fetch(`${API}/admin/blog/upload-video-chunk`, { method: "POST", body: fd });
        if (!r.ok) throw new Error("upload failed");
        const d = await r.json();
        setVideoProgress(Math.round(((i + 1) / total) * 100));
        if (d.status === "done") url = d.url;
      }
      if (url) setEditing(p => ({ ...p, videos: [...(p.videos || []), { url, caption: "" }] }));
    } catch {
      alert("Nie udało się wgrać filmu. Spróbuj ponownie.");
    }
    setUploading("");
    setVideoProgress(0);
    if (videoRef.current) videoRef.current.value = "";
  };

  const savePost = async () => {
    if (!editing.title.trim()) { alert("Tytuł jest wymagany"); return; }
    setSaving(true);
    const isNew = !editing.id;
    const r = await fetch(isNew ? `${API}/admin/blog` : `${API}/admin/blog/${editing.id}`, {
      method: isNew ? "POST" : "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(editing),
    });
    const saved = await r.json();
    setSaving(false);
    setEditing(null);
    fetchPosts();
    return saved;
  };

  const deletePost = async (id) => {
    if (!window.confirm("Usunąć wpis? Tej operacji nie można cofnąć.")) return;
    await fetch(`${API}/admin/blog/${id}`, { method: "DELETE" });
    setPosts(p => p.filter(x => x.id !== id));
  };

  const togglePublish = async (post) => {
    const newStatus = post.status === "published" ? "draft" : "published";
    await fetch(`${API}/admin/blog/${post.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: newStatus }),
    });
    setPosts(p => p.map(x => x.id === post.id ? { ...x, status: newStatus } : x));
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#0B0B0D] flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm bg-[#141416] border border-[#2A2A2D] p-8">
          <div className="flex items-center gap-3 mb-2">
            <Lock className="w-5 h-5 text-[#D4AF37]" />
            <h1 className="text-[#F5F5F5] text-lg tracking-wider">Panel redakcyjny</h1>
          </div>
          <p className="text-[#B8B8B8]/40 text-xs mb-6">Z życia hodowli — dostęp wewnętrzny</p>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B8B8B8]/40" />
              <input type="text" placeholder="Login" value={loginForm.login} onChange={e => setLoginForm(p => ({ ...p, login: e.target.value }))}
                className="w-full bg-[#0B0B0D] border border-[#2A2A2D] text-[#F5F5F5] text-sm pl-10 pr-4 py-3 focus:border-[#D4AF37] outline-none" data-testid="blog-admin-login-input" />
            </div>
            <input type="password" placeholder="Hasło" value={loginForm.password} onChange={e => setLoginForm(p => ({ ...p, password: e.target.value }))}
              className="w-full bg-[#0B0B0D] border border-[#2A2A2D] text-[#F5F5F5] text-sm px-4 py-3 focus:border-[#D4AF37] outline-none" data-testid="blog-admin-password-input" />
            {loginError && <p className="text-red-400 text-xs">{loginError}</p>}
            <button type="submit" className="w-full bg-[#D4AF37] text-[#0B0B0D] py-3 text-sm font-semibold tracking-wider hover:bg-[#E8C547] transition-colors" data-testid="blog-admin-login-btn">ZALOGUJ</button>
          </form>
        </motion.div>
      </div>
    );
  }

  // EDITOR VIEW
  if (editing) {
    return (
      <div className="min-h-screen bg-[#0B0B0D] text-[#F5F5F5] px-4 md:px-8 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-8 gap-3 flex-wrap">
            <button onClick={() => setEditing(null)} className="flex items-center gap-2 text-[#B8B8B8] text-sm hover:text-[#D4AF37]">
              <ArrowLeft className="w-4 h-4" /> Wróć do listy
            </button>
            <div className="flex gap-3">
              <button onClick={savePost} disabled={saving} data-testid="blog-save-btn"
                className="flex items-center gap-2 px-6 py-2.5 bg-[#D4AF37] text-[#0B0B0D] text-xs font-bold tracking-wider hover:bg-[#E8C547] disabled:opacity-50">
                <Save className="w-4 h-4" /> {saving ? "ZAPISYWANIE…" : "ZAPISZ WPIS"}
              </button>
            </div>
          </div>

          <h2 className="font-serif text-2xl mb-1">{editing.id ? "Edytuj wpis" : "Nowy wpis"}</h2>
          <p className="text-[#B8B8B8]/40 text-xs mb-8">Status: <span className={editing.status === "published" ? "text-green-400" : "text-amber-400"}>{editing.status === "published" ? "OPUBLIKOWANY" : "SZKIC (niewidoczny publicznie)"}</span></p>

          {/* Treść */}
          <div className="bg-[#141416] border border-[#2A2A2D] p-6 mb-6">
            <h3 className="text-[10px] tracking-[0.3em] text-[#D4AF37] mb-5 uppercase flex items-center gap-2"><FileText className="w-3.5 h-3.5" /> Treść wpisu</h3>
            <Field label="Tytuł (H1)"><input value={editing.title} onChange={e => setEditing(p => ({ ...p, title: e.target.value }))} className={inputCls} data-testid="blog-field-title" /></Field>
            <Field label="Slug (URL)" hint="Zostaw puste, aby wygenerować z tytułu. Adres: /z-zycia-hodowli/slug">
              <input value={editing.slug} onChange={e => setEditing(p => ({ ...p, slug: e.target.value }))} className={inputCls} placeholder="socjalizacja-szczeniat" data-testid="blog-field-slug" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Kategoria">
                <select value={editing.category} onChange={e => setEditing(p => ({ ...p, category: e.target.value }))} className={inputCls} data-testid="blog-field-category">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Data publikacji"><input type="date" value={editing.date} onChange={e => setEditing(p => ({ ...p, date: e.target.value }))} className={inputCls} data-testid="blog-field-date" /></Field>
            </div>
            <Field label="Zajawka / excerpt" hint="Krótki opis na karcie listy i w meta description (jeśli puste).">
              <textarea value={editing.excerpt} onChange={e => setEditing(p => ({ ...p, excerpt: e.target.value }))} rows={2} className={inputCls} data-testid="blog-field-excerpt" />
            </Field>
            <Field label="Treść" hint="Akapity oddzielaj pustą linią. Nagłówki: '## H2', '### H3'.">
              <textarea value={editing.content} onChange={e => setEditing(p => ({ ...p, content: e.target.value }))} rows={12} className={inputCls + " font-mono text-[13px] leading-relaxed"} data-testid="blog-field-content" />
            </Field>
          </div>

          {/* Zdjęcia */}
          <div className="bg-[#141416] border border-[#2A2A2D] p-6 mb-6">
            <h3 className="text-[10px] tracking-[0.3em] text-[#D4AF37] mb-5 uppercase flex items-center gap-2"><ImagePlus className="w-3.5 h-3.5" /> Zdjęcia</h3>
            <Field label="Zdjęcie główne (cover)">
              {editing.cover_image && <img src={editing.cover_image} alt="" className="w-full max-w-xs rounded-sm border border-[#2A2A2D] mb-3" />}
              <button onClick={() => coverRef.current?.click()} disabled={uploading === "cover"}
                className="flex items-center gap-2 px-4 py-2 border border-[#2A2A2D] text-xs hover:border-[#D4AF37] disabled:opacity-50" data-testid="blog-cover-upload">
                <Upload className="w-3.5 h-3.5" /> {uploading === "cover" ? "WGRYWANIE…" : editing.cover_image ? "ZMIEŃ ZDJĘCIE" : "WGRAJ ZDJĘCIE"}
              </button>
              <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={e => handleCover(e.target.files[0])} />
            </Field>
            <Field label="Opis ALT zdjęcia głównego" hint="Ważne dla SEO. Opisz co jest na zdjęciu.">
              <input value={editing.image_alt_text} onChange={e => setEditing(p => ({ ...p, image_alt_text: e.target.value }))} className={inputCls} data-testid="blog-field-cover-alt" />
            </Field>

            <Field label="Galeria zdjęć">
              {(editing.gallery || []).length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                  {editing.gallery.map((g, i) => (
                    <div key={i} className="relative" data-testid={`blog-gallery-item-${i}`}>
                      <div className="relative aspect-square overflow-hidden bg-[#0B0B0D]">
                        <img src={g.url} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => setEditing(p => ({ ...p, gallery: p.gallery.filter((_, idx) => idx !== i) }))}
                          className="absolute top-1 right-1 w-7 h-7 bg-red-900/80 text-red-200 rounded-full flex items-center justify-center hover:bg-red-800" data-testid={`blog-gallery-del-${i}`}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <input value={g.alt} onChange={e => { const v = e.target.value; setEditing(p => ({ ...p, gallery: p.gallery.map((x, idx) => idx === i ? { ...x, alt: v } : x) })); }}
                        placeholder="Opis ALT…" className="w-full mt-1 bg-[#0B0B0D] border border-[#2A2A2D] text-[#F5F5F5] text-[11px] px-2 py-1 focus:border-[#D4AF37] outline-none" data-testid={`blog-gallery-alt-${i}`} />
                    </div>
                  ))}
                </div>
              )}
              <button onClick={() => galleryRef.current?.click()} disabled={uploading === "gallery"}
                className="flex items-center gap-2 px-4 py-2 border border-[#2A2A2D] text-xs hover:border-[#D4AF37] disabled:opacity-50" data-testid="blog-gallery-upload">
                <Upload className="w-3.5 h-3.5" /> {uploading === "gallery" ? "WGRYWANIE…" : "DODAJ ZDJĘCIA DO GALERII"}
              </button>
              <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={e => handleGallery(Array.from(e.target.files))} />
            </Field>

            <Field label="Filmy we wpisie" hint="MP4/WebM/MOV. Film pokaże się we wpisie z odtwarzaczem.">
              {(editing.videos || []).length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                  {editing.videos.map((v, i) => (
                    <div key={i} className="relative" data-testid={`blog-video-item-${i}`}>
                      <div className="relative aspect-video overflow-hidden bg-[#0B0B0D] border border-[#2A2A2D]">
                        <video src={v.url} controls preload="metadata" className="w-full h-full object-contain" />
                        <button onClick={() => setEditing(p => ({ ...p, videos: p.videos.filter((_, idx) => idx !== i) }))}
                          className="absolute top-1 right-1 z-10 w-7 h-7 bg-red-900/80 text-red-200 rounded-full flex items-center justify-center hover:bg-red-800" data-testid={`blog-video-del-${i}`}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <input value={v.caption} onChange={e => { const val = e.target.value; setEditing(p => ({ ...p, videos: p.videos.map((x, idx) => idx === i ? { ...x, caption: val } : x) })); }}
                        placeholder="Podpis filmu…" className="w-full mt-1 bg-[#0B0B0D] border border-[#2A2A2D] text-[#F5F5F5] text-[11px] px-2 py-1 focus:border-[#D4AF37] outline-none" data-testid={`blog-video-caption-${i}`} />
                    </div>
                  ))}
                </div>
              )}
              <button onClick={() => videoRef.current?.click()} disabled={uploading === "video"}
                className="flex items-center gap-2 px-4 py-2 border border-[#2A2A2D] text-xs hover:border-[#D4AF37] disabled:opacity-50" data-testid="blog-video-upload">
                <Upload className="w-3.5 h-3.5" /> {uploading === "video" ? `WGRYWANIE… ${videoProgress}%` : "DODAJ FILM"}
              </button>
              <input ref={videoRef} type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={e => handleVideo(e.target.files[0])} />
            </Field>
          </div>

          {/* SEO / OG */}
          <div className="bg-[#141416] border border-[#2A2A2D] p-6 mb-6">
            <h3 className="text-[10px] tracking-[0.3em] text-[#D4AF37] mb-5 uppercase">SEO i Facebook (Open Graph)</h3>
            <Field label="SEO Title" hint="Tytuł w Google. Jeśli puste, użyje tytułu wpisu."><input value={editing.seo_title} onChange={e => setEditing(p => ({ ...p, seo_title: e.target.value }))} className={inputCls} data-testid="blog-field-seo-title" /></Field>
            <Field label="Meta Description" hint="Opis w Google (~155 znaków). Jeśli puste, użyje zajawki."><textarea value={editing.seo_description} onChange={e => setEditing(p => ({ ...p, seo_description: e.target.value }))} rows={2} className={inputCls} data-testid="blog-field-seo-desc" /></Field>
            <Field label="OG Title (Facebook)" hint="Jeśli puste, użyje SEO Title / tytułu."><input value={editing.og_title} onChange={e => setEditing(p => ({ ...p, og_title: e.target.value }))} className={inputCls} /></Field>
            <Field label="OG Description (Facebook)" hint="Jeśli puste, użyje meta description."><textarea value={editing.og_description} onChange={e => setEditing(p => ({ ...p, og_description: e.target.value }))} rows={2} className={inputCls} /></Field>
            <Field label="OG Image (Facebook)" hint="Zdjęcie przy udostępnieniu. Jeśli puste, użyje zdjęcia głównego.">
              {editing.og_image && <img src={editing.og_image} alt="" className="w-full max-w-xs rounded-sm border border-[#2A2A2D] mb-3" />}
              <button onClick={() => ogRef.current?.click()} disabled={uploading === "og"}
                className="flex items-center gap-2 px-4 py-2 border border-[#2A2A2D] text-xs hover:border-[#D4AF37] disabled:opacity-50">
                <Upload className="w-3.5 h-3.5" /> {uploading === "og" ? "WGRYWANIE…" : "WGRAJ OG IMAGE"}
              </button>
              <input ref={ogRef} type="file" accept="image/*" className="hidden" onChange={e => handleOg(e.target.files[0])} />
            </Field>
          </div>

          {/* Publikacja */}
          <div className="bg-[#141416] border border-[#2A2A2D] p-6 mb-10">
            <h3 className="text-[10px] tracking-[0.3em] text-[#D4AF37] mb-5 uppercase">Publikacja</h3>
            <div className="flex gap-3">
              <button onClick={() => setEditing(p => ({ ...p, status: "draft" }))}
                className={`flex-1 py-3 text-xs tracking-wider border ${editing.status === "draft" ? "border-amber-400 text-amber-400 bg-amber-400/5" : "border-[#2A2A2D] text-[#B8B8B8]"}`} data-testid="blog-status-draft">
                SZKIC
              </button>
              <button onClick={() => setEditing(p => ({ ...p, status: "published" }))}
                className={`flex-1 py-3 text-xs tracking-wider border ${editing.status === "published" ? "border-green-400 text-green-400 bg-green-400/5" : "border-[#2A2A2D] text-[#B8B8B8]"}`} data-testid="blog-status-published">
                OPUBLIKOWANY
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // LIST VIEW
  return (
    <div className="min-h-screen bg-[#0B0B0D] text-[#F5F5F5] px-4 md:px-8 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8 gap-3 flex-wrap">
          <div>
            <h1 className="font-serif text-2xl tracking-wider flex items-center gap-2"><FileText className="w-6 h-6 text-[#D4AF37]" /> Z życia hodowli</h1>
            <p className="text-[#B8B8B8]/40 text-xs mt-1">Panel redakcyjny — dostęp wewnętrzny</p>
          </div>
          <div className="flex gap-3 items-center">
            <a href="/z-zycia-hodowli" target="_blank" rel="noopener noreferrer" className="px-4 py-2 border border-[#2A2A2D] text-[#B8B8B8] text-xs tracking-wider hover:border-[#D4AF37] hover:text-[#D4AF37] flex items-center gap-1.5"><ExternalLink className="w-3.5 h-3.5" /> STRONA</a>
            <button onClick={() => setEditing(emptyPost())} data-testid="blog-add-btn"
              className="flex items-center gap-2 px-5 py-2.5 bg-[#D4AF37] text-[#0B0B0D] text-xs font-bold tracking-wider hover:bg-[#E8C547]">
              <Plus className="w-4 h-4" /> NOWY WPIS
            </button>
          </div>
        </div>

        {posts.length === 0 && <p className="text-[#B8B8B8]/40 text-sm">Brak wpisów. Kliknij „NOWY WPIS", aby utworzyć pierwszy.</p>}

        <div className="space-y-3" data-testid="blog-admin-list">
          {posts.map(post => (
            <div key={post.id} className="bg-[#141416] border border-[#2A2A2D] p-4 flex items-center gap-4" data-testid={`blog-admin-item-${post.slug}`}>
              <div className="w-16 h-16 shrink-0 bg-[#0B0B0D] overflow-hidden">
                {post.cover_image ? <img src={post.cover_image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#2A2A2D]"><ImagePlus className="w-5 h-5" /></div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`text-[9px] px-2 py-0.5 tracking-wider uppercase ${post.status === "published" ? "bg-green-400/10 text-green-400" : "bg-amber-400/10 text-amber-400"}`}>{post.status === "published" ? "Opublikowany" : "Szkic"}</span>
                  <span className="text-[10px] text-[#D4AF37]/60 uppercase tracking-wider">{post.category}</span>
                  <span className="text-[10px] text-[#B8B8B8]/30">{post.date}</span>
                </div>
                <p className="text-sm text-[#F5F5F5] truncate font-serif">{post.title}</p>
                <p className="text-[11px] text-[#B8B8B8]/30 truncate">/z-zycia-hodowli/{post.slug}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => togglePublish(post)} title={post.status === "published" ? "Cofnij do szkicu" : "Opublikuj"}
                  className="px-3 py-1.5 border border-[#2A2A2D] text-xs hover:border-[#D4AF37]/40" data-testid={`blog-toggle-${post.slug}`}>
                  {post.status === "published" ? <Eye className="w-3.5 h-3.5 text-green-400" /> : <EyeOff className="w-3.5 h-3.5 text-[#B8B8B8]/50" />}
                </button>
                <button onClick={() => setEditing({ ...emptyPost(), ...post })} className="px-3 py-1.5 bg-[#D4AF37] text-[#0B0B0D] text-xs font-semibold hover:bg-[#E8C547]" data-testid={`blog-edit-${post.slug}`}>EDYTUJ</button>
                <button onClick={() => deletePost(post.id)} className="px-3 py-1.5 border border-red-700/40 text-red-300 text-xs hover:bg-red-900/30" data-testid={`blog-delete-${post.slug}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default KennelAdminBlog;
